import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Trophy, User, Mail, DollarSign, Check, X, RefreshCw, ChevronDown, ChevronUp, Download } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminContest = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [stats, setStats] = useState({ total: 0, paid: 0, unpaid: 0, totalRevenue: 0 });

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/contest/entries`);
      const data = response.data;
      setEntries(data);
      
      // Calculate stats
      const paid = data.filter(e => e.paid).length;
      setStats({
        total: data.length,
        paid: paid,
        unpaid: data.length - paid,
        totalRevenue: paid * 5
      });
    } catch (err) {
      toast.error("Error fetching entries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  const togglePaid = async (entryId, currentPaid) => {
    try {
      if (!currentPaid) {
        await axios.patch(`${BACKEND_URL}/api/contest/entries/${entryId}/paid`);
        toast.success("Marked as paid");
      }
      fetchEntries();
    } catch (err) {
      toast.error("Error updating entry");
    }
  };

  const exportToCSV = () => {
    if (entries.length === 0) {
      toast.error("No entries to export");
      return;
    }

    // Create CSV header
    const headers = ["Entry #", "Name", "Email", "Payment Method", "Paid", "Submitted", "Breed Guesses"];
    
    // Create CSV rows
    const rows = entries.map((entry, index) => {
      const guessesStr = entry.guesses
        ?.map(g => `${g.breed}: ${g.percentage}%`)
        .join("; ") || "";
      
      return [
        index + 1,
        `"${entry.name}"`,
        `"${entry.email}"`,
        entry.payment_method,
        entry.paid ? "Yes" : "No",
        new Date(entry.created_at).toLocaleString(),
        `"${guessesStr}"`
      ].join(",");
    });

    // Combine header and rows
    const csvContent = [headers.join(","), ...rows].join("\n");
    
    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `contest_entries_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success("Exported to CSV");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="min-h-screen bg-[#0D0D0B] pt-20 pb-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <Trophy className="w-8 h-8 text-[#FFD700]" />
            <h1 className="font-['Bebas_Neue'] text-4xl text-white">
              Contest Entries
            </h1>
          </div>
          <p className="text-[#A3A3A3]">
            Manage breed guessing contest submissions
          </p>
          
          <div className="mt-4 flex gap-4">
            <Link to="/contest" className="text-[#FFD700] text-sm hover:text-white">
              ← Back to Contest Page
            </Link>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
        >
          <div className="card-tactical p-4 text-center">
            <p className="text-[#666] text-xs font-mono uppercase tracking-widest mb-1">Total Entries</p>
            <p className="font-['Bebas_Neue'] text-3xl text-white">{stats.total}</p>
          </div>
          <div className="card-tactical p-4 text-center">
            <p className="text-[#666] text-xs font-mono uppercase tracking-widest mb-1">Paid</p>
            <p className="font-['Bebas_Neue'] text-3xl text-green-400">{stats.paid}</p>
          </div>
          <div className="card-tactical p-4 text-center">
            <p className="text-[#666] text-xs font-mono uppercase tracking-widest mb-1">Unpaid</p>
            <p className="font-['Bebas_Neue'] text-3xl text-yellow-400">{stats.unpaid}</p>
          </div>
          <div className="card-tactical p-4 text-center">
            <p className="text-[#666] text-xs font-mono uppercase tracking-widest mb-1">Revenue</p>
            <p className="font-['Bebas_Neue'] text-3xl text-[#FFD700]">${stats.totalRevenue}</p>
          </div>
        </motion.div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 mb-4">
          <button
            onClick={exportToCSV}
            disabled={entries.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-[#4A5320] text-white hover:bg-[#5a6330] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            Export to CSV
          </button>
          <button
            onClick={fetchEntries}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 border border-[#4A5320] text-[#A3A3A3] hover:text-white hover:border-[#FFD700] transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Entries List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card-tactical"
        >
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-8 h-8 text-[#FFD700] animate-spin mx-auto mb-4" />
              <p className="text-[#A3A3A3]">Loading entries...</p>
            </div>
          ) : entries.length === 0 ? (
            <div className="p-8 text-center">
              <Trophy className="w-12 h-12 text-[#4A5320] mx-auto mb-4" />
              <p className="text-[#A3A3A3]">No contest entries yet</p>
            </div>
          ) : (
            <div className="divide-y divide-[#4A5320]">
              {entries.map((entry, index) => (
                <div key={entry.id} className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-[#666] text-xs font-mono">#{index + 1}</span>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-[#666]" />
                          <span className="text-white font-semibold">{entry.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-[#666]" />
                          <span className="text-[#A3A3A3] text-sm">{entry.email}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4 text-xs text-[#666]">
                        <span>Payment: {entry.payment_method}</span>
                        <span>Submitted: {formatDate(entry.created_at)}</span>
                      </div>

                      {/* Expandable Guesses */}
                      <button
                        onClick={() => setExpandedEntry(expandedEntry === entry.id ? null : entry.id)}
                        className="mt-2 flex items-center gap-1 text-[#FFD700] text-sm hover:text-white"
                      >
                        {expandedEntry === entry.id ? (
                          <>
                            <ChevronUp className="w-4 h-4" /> Hide Guesses
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-4 h-4" /> Show {entry.guesses?.length || 0} Breed Guesses
                          </>
                        )}
                      </button>

                      {expandedEntry === entry.id && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          className="mt-3 p-3 bg-[#0A0A0A] border border-[#333] rounded"
                        >
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-[#666] border-b border-[#333]">
                                <th className="pb-2">Breed</th>
                                <th className="pb-2 text-right">Percentage</th>
                              </tr>
                            </thead>
                            <tbody>
                              {entry.guesses?.map((guess, gIdx) => (
                                <tr key={gIdx} className="border-b border-[#222] last:border-0">
                                  <td className="py-2 text-white">{guess.breed}</td>
                                  <td className="py-2 text-right text-[#FFD700] font-mono">{guess.percentage}%</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="text-[#A3A3A3] font-semibold">
                                <td className="pt-2">Total</td>
                                <td className="pt-2 text-right font-mono">
                                  {entry.guesses?.reduce((sum, g) => sum + g.percentage, 0)}%
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </motion.div>
                      )}
                    </div>

                    {/* Payment Status */}
                    <div className="flex-shrink-0">
                      <button
                        onClick={() => togglePaid(entry.id, entry.paid)}
                        className={`flex items-center gap-2 px-3 py-2 border transition-colors ${
                          entry.paid
                            ? "border-green-700 bg-green-900/30 text-green-400"
                            : "border-yellow-700 bg-yellow-900/20 text-yellow-400 hover:bg-yellow-900/40"
                        }`}
                      >
                        {entry.paid ? (
                          <>
                            <Check className="w-4 h-4" />
                            <span className="text-sm font-mono">PAID</span>
                          </>
                        ) : (
                          <>
                            <DollarSign className="w-4 h-4" />
                            <span className="text-sm font-mono">MARK PAID</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminContest;
